interface DefaultOutboundViewShape {
  position: number;
  folderId?: string | null;
}
interface DefaultOutboundFolderShape {
  _id: string;
  position: number;
}

/**
 * Picks the view a user should land on by default. Walks the team's nav order
 * — top-level views and folders sorted together by position — and returns the
 * first reachable view, diving into the first folder when one sorts above any
 * top-level view. Returns undefined if the team has no reachable view.
 *
 * Generic over the view shape so callers passing the full FulfillmentGroupView
 * get one back; tests can pass minimal `{ position, folderId }` objects.
 */
export function findDefaultOutboundView<V extends DefaultOutboundViewShape>({
  views,
  folders,
}: {
  views: readonly V[];
  folders: readonly DefaultOutboundFolderShape[];
}): V | undefined {
  type TopLevelItem =
    | { kind: "view"; position: number; view: V }
    | { kind: "folder"; position: number; folder: DefaultOutboundFolderShape };

  const items: TopLevelItem[] = [
    ...views
      .filter((v) => !v.folderId)
      .map<TopLevelItem>((v) => ({
        kind: "view",
        position: v.position,
        view: v,
      })),
    ...folders.map<TopLevelItem>((f) => ({
      kind: "folder",
      position: f.position,
      folder: f,
    })),
  ].sort((a, b) => a.position - b.position);

  for (const item of items) {
    if (item.kind === "view") {
      return item.view;
    }
    const folderViews = views
      .filter((v) => v.folderId === item.folder._id)
      .sort((a, b) => a.position - b.position);
    if (folderViews[0]) {
      return folderViews[0];
    }
  }
  return undefined;
}
